export const isProduction = import.meta.env.MODE === 'production';

export const keyMapMetadata: Record<string, string> = {
  category: 'Thư mục gốc',
  fileName: 'Tên tệp',
  fullPath: 'Đường dẫn đầy đủ',
  createAt: 'Ngày tạo',
  creator: 'Người tạo',
  creatorId: 'ID người tạo',
  // Người sửa đổi hiển thị ngay sau Người tạo.
  modifier: 'Người sửa đổi lần cuối',
  modifierId: 'ID người sửa đổi',
  modifiedDate: 'Ngày sửa đổi',
  DataSource: 'Nguồn dữ liệu',
  SuggestedAICategories: 'Danh mục AI gợi ý',
  KeywordAISuggestion: 'Từ khóa AI gợi ý',
  SummaryOfSuggestedAI: 'Tóm tắt AI gợi ý',
  AccessLevel: 'Cấp độ truy cập',
  path: 'Đường dẫn',
  summary: 'Trích yếu',
  effectiveDate: 'Ngày hiệu lực',
  expirationDate: 'Ngày hết hiệu lực',
  issueDate: 'Ngày ban hành',
  processingStatus: 'Trạng thái xử lý',
  status: 'Tình trạng',
  version: 'Phiên bản',
  documentType: 'Loại văn bản',
  field: 'Lĩnh vực',
  dataSource: 'Nguồn dữ liệu',
};

/**
 * Các trường do server quản lý. Client chỉ hiển thị, không gửi lên khi lưu:
 * server tự xác định người tạo / người sửa từ tài khoản đang thao tác.
 */
export const SERVER_OWNED_METADATA_KEYS = [
  'creator',
  'creatorId',
  'modifier',
  'modifierId',
  'modifiedDate',
] as const;

const isBlankValue = (value: unknown): boolean =>
  value === null || value === undefined || `${value}`.trim() === '' || `${value}`.trim() === '---';

/**
 * Chuẩn hoá metadata đọc từ S3 trước khi đưa vào form sửa.
 *
 * Tài liệu cũ chưa có `modifier` / `modifiedDate`: chèn các trường này ngay sau
 * `creatorId` và mặc định lấy theo người tạo, để UI luôn hiển thị đủ thông tin
 * theo đúng thứ tự mong muốn (Người tạo -> Người sửa đổi lần cuối).
 */
export const normalizeMetadataForDisplay = (
  metadata: Record<string, any> | null | undefined,
): Record<string, any> => {
  if (!metadata || typeof metadata !== 'object') return {};

  const creator = isBlankValue(metadata['creator']) ? '' : `${metadata['creator']}`.trim();
  const creatorId = isBlankValue(metadata['creatorId']) ? '' : `${metadata['creatorId']}`.trim();

  // Rỗng thì mặc định là Người tạo.
  const modifier = isBlankValue(metadata['modifier']) ? creator : `${metadata['modifier']}`.trim();
  const modifierId = isBlankValue(metadata['modifierId'])
    ? creatorId
    : `${metadata['modifierId']}`.trim();
  const modifiedDate = isBlankValue(metadata['modifiedDate'])
    ? isBlankValue(metadata['createAt'])
      ? ''
      : `${metadata['createAt']}`.trim()
    : `${metadata['modifiedDate']}`.trim();

  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(metadata)) {
    if (key === 'modifier' || key === 'modifierId' || key === 'modifiedDate') continue;

    result[key] = value;

    if (key === 'creatorId') {
      result['modifier'] = modifier;
      result['modifierId'] = modifierId;
      result['modifiedDate'] = modifiedDate;
    }
  }

  // Sidecar cũ không có `creatorId` -> bổ sung ở cuối để không mất thông tin.
  if (!('modifier' in result)) {
    result['modifier'] = modifier;
    result['modifierId'] = modifierId;
    result['modifiedDate'] = modifiedDate;
  }

  return result;
};

/**
 * Loại bỏ các trường do server quản lý khỏi payload lưu metadata.
 * Nhờ vậy người tạo không bị ghi đè và người sửa luôn do server đóng dấu.
 */
export const stripServerOwnedMetadata = (
  metadata: Record<string, any> | null | undefined,
): Record<string, any> => {
  if (!metadata || typeof metadata !== 'object') return {};

  const result: Record<string, any> = { ...metadata };
  for (const key of SERVER_OWNED_METADATA_KEYS) {
    delete result[key];
  }
  return result;
};

export const isTextarea = (key: string) => {
  if (
    key === 'SummaryOfSuggestedAI' ||
    key === 'KeywordAISuggestion' ||
    key === 'SuggestedAICategories'
  ) {
    return true;
  }

  return false;
};

export const isDisableField = (key: string) => {
  if (
    key === 'fullPath' ||
    key === 'createAt' ||
    key === 'creator' ||
    key === 'creatorId' ||
    // Người sửa đổi do server tự cập nhật theo tài khoản thao tác -> chỉ đọc.
    key === 'modifier' ||
    key === 'modifierId' ||
    key === 'modifiedDate' ||
    key === 'DataSource' ||
    key === 'AccessLevel' ||
    key === 'category' ||
    key === 'fileName' ||
    key === 'version'
  ) {
    return true;
  }

  return false;
};

// Giới hạn tổng dung lượng file trong mỗi lần upload là 100MB.
// Không giới hạn số lượng file và không giới hạn riêng từng file; một file lớn hơn
// 100MB vẫn bị từ chối vì làm tổng dung lượng lần upload vượt quá giới hạn.
export const MAX_UPLOAD_TOTAL_SIZE = 100 * 1024 * 1024;

export const checkConditionUploadFiles = (files: FileList, toast: any) => {
  const totalSize = Array.from(files).reduce((acc, file) => acc + (file?.size ?? 0), 0);
  if (totalSize > MAX_UPLOAD_TOTAL_SIZE) {
    toast({
      title: 'Lỗi tải lên',
      description: 'Tổng dung lượng các tập tin vượt quá giới hạn 100MB.',
      variant: 'destructive',
    });
    return true;
  }

  return false;
};

// Map sync status from English to Vietnamese
export const syncStatusMap: Record<string, string> = {
  STARTING: 'Đang bắt đầu',
  IN_PROGRESS: 'Đang đồng bộ',
  COMPLETE: 'Đồng bộ thành công',
  FAILED: 'Đồng bộ thất bại',
  STOPPING: 'Đang dừng',
  STOPPED: 'Đã dừng',
};

export const getSyncStatusInVietnamese = (status: string): string => {
  return syncStatusMap[status] || status;
};
